import React from "react";
import { Tooltip } from "@mui/material";
/**
 * Smart truncation utility that returns a string (for use with highlighting)
 * @param text - The text to truncate
 * @param maxLength - Maximum character length
 * @returns Truncated string
 */
export const smartTruncateString = (text, maxLength = 50) => {
    if (!text)
        return text;
    // If text is short enough, return as is
    if (text.length <= maxLength) {
        return text;
    }
    // Split by common separators to identify hierarchy parts
    const separators = ["/", "\\", " > ", " → ", " - "];
    let parts = [text];
    for (const separator of separators) {
        if (text.includes(separator)) {
            parts = text.split(separator);
            break;
        }
    }
    // If no separators found, do simple truncation
    if (parts.length === 1) {
        return text.length > maxLength
            ? text.substring(0, maxLength - 3) + "..."
            : text;
    }
    // Smart truncation for hierarchical text
    return smartTruncateHierarchy(parts, maxLength);
};
/**
 * Smart truncation utility that intelligently truncates text based on available space
 * and shows ellipsis for intermediate parts while keeping the final part visible
 * @param text - The text to truncate
 * @param maxWidth - Maximum width in pixels (optional)
 * @param maxLength - Maximum character length (optional)
 * @param showTooltip - Whether to show tooltip with full text
 * @returns JSX element with smart truncation
 */
export const smartTruncate = ({ text, maxWidth = 200, maxLength = 50, showTooltip = true, }) => {
    if (!text)
        return text;
    // If text is short enough, return as is
    if (text.length <= maxLength) {
        return showTooltip ? (React.createElement(Tooltip, { title: text, arrow: true },
            React.createElement("span", null, text))) : (text);
    }
    // Split by common separators to identify hierarchy parts
    const separators = ["/", "\\", " > ", " → ", " - "];
    let parts = [text];
    for (const separator of separators) {
        if (text.includes(separator)) {
            parts = text.split(separator);
            break;
        }
    }
    // If no separators found, do simple truncation
    if (parts.length === 1) {
        const truncated = text.length > maxLength ? text.substring(0, maxLength - 3) + "..." : text;
        return showTooltip ? (React.createElement(Tooltip, { title: text, arrow: true },
            React.createElement("span", { style: {
                    maxWidth,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                } }, truncated))) : (React.createElement("span", { style: {
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            } }, truncated));
    }
    // Smart truncation for hierarchical text
    const result = smartTruncateHierarchy(parts, maxLength);
    return showTooltip ? (React.createElement(Tooltip, { title: text, arrow: true },
        React.createElement("span", { style: {
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            } }, result))) : (React.createElement("span", { style: {
            maxWidth,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        } }, result));
};
/**
 * Smart truncation for hierarchical text
 * Keeps the final part visible and truncates intermediate parts with ellipsis
 * NEVER truncates the final project name
 */
const smartTruncateHierarchy = (parts, maxLength) => {
    if (parts.length === 1)
        return parts[0];
    const finalPart = parts[parts.length - 1];
    const intermediateParts = parts.slice(0, -1);
    // If we have enough space for all parts, show them all
    const totalLength = parts.join("/").length;
    if (totalLength <= maxLength) {
        return parts.join("/");
    }
    // Calculate available space for intermediate parts
    // Reserve space for final part + separators, but be more flexible
    const reservedSpace = Math.min(finalPart.length + 6, maxLength * 0.4); // Reserve max 40% for final part
    const availableSpace = maxLength - reservedSpace;
    // If not enough space even for ellipsis, show appropriate number of levels
    if (availableSpace <= 3) {
        const ellipsis = "../".repeat(intermediateParts.length);
        return ellipsis + finalPart;
    }
    // Try to fit as many intermediate parts as possible, starting from the beginning
    let result = "";
    let currentLength = 0;
    let truncatedLevels = 0;
    for (let i = 0; i < intermediateParts.length; i++) {
        const part = intermediateParts[i];
        const separator = i === 0 ? "" : "/";
        const partWithSeparator = separator + part;
        if (currentLength + partWithSeparator.length <= availableSpace) {
            result += partWithSeparator;
            currentLength += partWithSeparator.length;
        }
        else {
            // Count how many levels we're truncating from the beginning
            truncatedLevels = intermediateParts.length - i;
            break;
        }
    }
    // Add the correct number of ellipsis at the beginning if we truncated from the start
    if (truncatedLevels > 0) {
        const ellipsis = "../".repeat(truncatedLevels);
        result = ellipsis + result;
    }
    return result + (result ? "/" : "") + finalPart;
};
/**
 * Hook for smart truncation with responsive behavior
 */
export const useSmartTruncation = (text, options) => {
    return React.useMemo(() => smartTruncate(Object.assign({ text }, options)), [text, options === null || options === void 0 ? void 0 : options.maxWidth, options === null || options === void 0 ? void 0 : options.maxLength, options === null || options === void 0 ? void 0 : options.showTooltip]);
};
